import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Waves, LogOut, LayoutDashboard, History, User, Gift, Megaphone, Briefcase, Wrench, MapPin, FileText, BarChart3, Package, TrendingUp, BookOpen, Mail, Inbox } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const fetchCount = () =>
      api.get("/admin/contact/unread-count").then((r) => setUnreadCount(r.data.count || 0)).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 120000);
    return () => clearInterval(t);
  }, [user?.role]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <span className="grid place-items-center h-9 w-9 rounded-md bg-secondary text-secondary-foreground shadow-sm">
            <Waves className="h-5 w-5" strokeWidth={2.4} />
          </span>
          <span className="font-display font-black tracking-tight text-lg">
            Docto<span className="text-primary">Spa</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/diagnostic" className={linkClass} data-testid="nav-diagnostic">Diagnostic</NavLink>
          <NavLink to="/technicians" className={linkClass} data-testid="nav-technicians">Annuaire</NavLink>
          <NavLink to="/annuaire-fabricants" className={linkClass} data-testid="nav-manufacturers">Fabricants</NavLink>
          <NavLink to="/pieces-detachees" className={linkClass} data-testid="nav-spa-parts">Pièces détachées</NavLink>
          <NavLink to="/codes-erreurs" className={linkClass} data-testid="nav-error-codes">Codes erreurs</NavLink>
          <NavLink to="/tutos" className={linkClass} data-testid="nav-tutos">Tutos</NavLink>
          <NavLink to="/kits" className={linkClass} data-testid="nav-kits">Kits</NavLink>
          <NavLink to="/blog" className={linkClass} data-testid="nav-blog">Blog</NavLink>
          <NavLink to="/pricing" className={linkClass} data-testid="nav-pricing">Tarifs</NavLink>
          <NavLink to="/faq" className={linkClass} data-testid="nav-faq">FAQ</NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about">À propos</NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full px-3 relative" data-testid="user-menu-trigger">
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold">
                    {(user.name?.[0] || user.email?.[0] || "U").toUpperCase()}
                  </span>
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 left-6 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center"
                      data-testid="unread-messages-dot"
                    >
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                  <span className="ml-2 hidden sm:inline text-sm">{user.name || user.email}</span>
                  {user.is_pro && (
                    <span
                      className="ml-2 hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-slate-950 uppercase tracking-wider"
                      data-testid="pro-badge"
                    >
                      Pro
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel className="text-xs">Connecté en tant que<br /><span className="font-semibold">{user.email}</span></DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-dashboard">
                  <Link to="/dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />Tableau de bord</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-history">
                  <Link to="/history"><History className="h-4 w-4 mr-2" />Historique</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-referrals">
                  <Link to="/referrals"><Gift className="h-4 w-4 mr-2" />Parrainage</Link>
                </DropdownMenuItem>
                {user.has_pro_access && (
                  <DropdownMenuItem asChild data-testid="menu-pro-space">
                    <Link to="/pro"><Briefcase className="h-4 w-4 mr-2" />Espace Pro</Link>
                  </DropdownMenuItem>
                )}
                {user.account_type === "pro" && (
                  <DropdownMenuItem asChild data-testid="menu-tech-profile">
                    <Link to="/technicians/register"><Wrench className="h-4 w-4 mr-2" />Ma fiche technicien</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <>
                    <DropdownMenuItem asChild data-testid="menu-admin-dashboard">
                      <Link to="/admin/dashboard"><BarChart3 className="h-4 w-4 mr-2" />Dashboard KPI (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-ads">
                      <Link to="/admin/advertisers"><Megaphone className="h-4 w-4 mr-2" />Annonceurs (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-technicians">
                      <Link to="/admin/technicians"><MapPin className="h-4 w-4 mr-2" />Techniciens (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-pro">
                      <Link to="/admin/pro"><Briefcase className="h-4 w-4 mr-2" />Base Pro (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-blog">
                      <Link to="/admin/blog"><FileText className="h-4 w-4 mr-2" />Blog (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-spa-parts">
                      <Link to="/admin/spa-parts"><Package className="h-4 w-4 mr-2" />Pièces détachées (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-affiliate">
                      <Link to="/admin/affiliate"><TrendingUp className="h-4 w-4 mr-2" />Affiliation (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-ads">
                      <Link to="/admin/ads"><Megaphone className="h-4 w-4 mr-2" />Publicités (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-tutorials">
                      <Link to="/admin/tutorials"><BookOpen className="h-4 w-4 mr-2" />Tutos (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-newsletter">
                      <Link to="/admin/newsletter"><Mail className="h-4 w-4 mr-2" />Newsletter (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-kits">
                      <Link to="/admin/kits"><Package className="h-4 w-4 mr-2" />Kits (admin)</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-admin-contact">
                      <Link to="/admin/contact">
                        <Inbox className="h-4 w-4 mr-2" />Messages (admin)
                        {unreadCount > 0 && (
                          <span
                            className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center"
                            data-testid="unread-messages-badge"
                          >
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem asChild data-testid="menu-account">
                  <Link to="/dashboard"><User className="h-4 w-4 mr-2" />Compte</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={doLogout} data-testid="menu-logout" className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="header-login-btn">Se connecter</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="rounded-full px-5" data-testid="header-register-btn">
                  Commencer
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
